import { hasAtMostDecimalPlaces } from '@payaca/utilities/mathUtilities';
import { z } from 'zod/v4';

export const DEPOSIT_VALUE_TYPES = ['amount', 'percentage'] as const;
export type DepositValueType = (typeof DEPOSIT_VALUE_TYPES)[number];

/**
 * Validation for the Proposal deposit form. Only the field matching the
 * selected valueType is validated — the other is cleared on toggle and
 * ignored on submit. The amount is in minor units, as CurrencyInput emits.
 * The server rejects zero deposits and percentages beyond 2 decimal places,
 * so both are caught here first.
 */
export const proposalDepositFormSchema = z.object({
  deposit: z
    .object({
      valueType: z.enum(DEPOSIT_VALUE_TYPES),
      amount: z.number().int().nullable(),
      percentage: z.number().nullable(),
    })
    .superRefine((deposit, ctx) => {
      if (deposit.valueType === 'amount') {
        if (deposit.amount == null || deposit.amount <= 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['amount'],
            message: 'Enter a deposit amount greater than zero',
          });
        }
        return;
      }

      if (deposit.percentage == null || deposit.percentage <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['percentage'],
          message: 'Enter a deposit percentage greater than zero',
        });
      } else if (deposit.percentage > 100) {
        ctx.addIssue({
          code: 'custom',
          path: ['percentage'],
          message: 'Deposit percentage must be 100% or less',
        });
      } else if (!hasAtMostDecimalPlaces(deposit.percentage, 2)) {
        ctx.addIssue({
          code: 'custom',
          path: ['percentage'],
          message: 'Deposit percentage can have at most 2 decimal places',
        });
      }
    }),
});

export type ProposalDepositFormValues = z.infer<
  typeof proposalDepositFormSchema
>;
